import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { useAxiosInstance } from "../../../services/Interceptor";
import {
  addCombineBundle,
  deleteCombineBundle,
  getAllCombineBundles,
  updateCombineBundle,
} from "../../../services/AdminService";
import { SearchEventsByParams } from "../../../services/EventsService";
import { utcToLocalDate } from "./CouponTable";
import DropdownWithSearch from "../../../components/common/DropdownWithSearch";

const NEVER_EXPIRATION_DATE = "0001-01-01";
const PERCENT_OFF = "Percent Off";
const AMOUNT_OFF = "Amount Off";

const EMPTY_FORM = {
  id: null,
  name: "",
  discountType: "",
  amount: "",
  expirationDate: "",
  selectedCombines: [],
};

const firstDefined = (...values) =>
  values.find((value) => value !== undefined && value !== null);

const getEventId = (event) =>
  firstDefined(
    event?.id,
    event?.ID,
    event?.eventId,
    event?.EventId,
    event?.eventID,
    event?.EventID,
    event?.combineId,
  );

const getEventDate = (event) =>
  firstDefined(
    event?.date,
    event?.eventDate,
    event?.combineDate,
    event?.startDate,
    "",
  );

const getEventName = (event) =>
  firstDefined(
    event?.name,
    event?.eventName,
    event?.combineName,
    `Combine #${getEventId(event)}`,
  );

const getBundleId = (bundle) =>
  firstDefined(
    bundle?.id,
    bundle?.ID,
    bundle?.combineBundleId,
    bundle?.combineBundleID,
    bundle?.bundleId,
    bundle?.bundleID,
  );

const getBundleCombines = (bundle) =>
  firstDefined(
    bundle?.combines,
    bundle?.Combines,
    bundle?.bundleCombines,
    bundle?.combineEvents,
    bundle?.events,
    bundle?.combineIds,
    bundle?.CombineIds,
    bundle?.eventIds,
    bundle?.EventIds,
    [],
  );

const getBundleCombineCount = (bundle, combines) =>
  firstDefined(
    bundle?.combineCount,
    bundle?.combinesCount,
    Array.isArray(combines) ? combines.length : 0,
  );

const getBundleExpirationDate = (bundle) =>
  firstDefined(
    bundle?.expirationDate,
    bundle?.ExpirationDate,
    bundle?.expiration,
    "",
  );

const parseDateOnly = (dateValue) => {
  if (!dateValue) {
    return null;
  }

  if (dateValue instanceof Date) {
    return new Date(
      dateValue.getFullYear(),
      dateValue.getMonth(),
      dateValue.getDate(),
    );
  }

  const dateString = String(dateValue);
  const isoDate = dateString.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (isoDate) {
    return new Date(
      Number(isoDate[1]),
      Number(isoDate[2]) - 1,
      Number(isoDate[3]),
    );
  }

  const parsed = new Date(dateString);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return new Date(parsed.getFullYear(), parsed.getMonth(), parsed.getDate());
};

const isTodayOrFuture = (dateValue) => {
  const eventDate = parseDateOnly(dateValue);
  if (!eventDate) {
    return false;
  }

  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  return eventDate >= today;
};

const isNeverExpiration = (dateValue) =>
  !dateValue || String(dateValue).startsWith(NEVER_EXPIRATION_DATE);

const getLocalToday = () => {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  const day = String(now.getDate()).padStart(2, "0");
  return `${now.getFullYear()}-${month}-${day}`;
};

const formatAmount = (amount) => {
  const value = Number(amount || 0);
  return Number.isInteger(value) ? String(value) : value.toFixed(2);
};

const formatDiscount = ({ amount, discountType }) =>
  discountType === PERCENT_OFF
    ? `${formatAmount(amount)}%`
    : `$${Number(amount || 0).toFixed(2)}`;

const toTitleCase = (str = "") =>
  String(str)
    .toLowerCase()
    .replace(/\b\w/g, (char) => char.toUpperCase());

const formatCombineLabel = (combine) => {
  const location = [combine.city, combine.state].filter(Boolean).join(", ");
  const label = [combine.date, combine.name, location]
    .filter(Boolean)
    .join(" - ");
  return toTitleCase(label);
};

const normalizeEvent = (rawEvent, knownEvents = []) => {
  const nestedEvent =
    rawEvent?.event ?? rawEvent?.combine ?? rawEvent?.combineEvent;

  if (nestedEvent) {
    return normalizeEvent(nestedEvent, knownEvents);
  }

  if (typeof rawEvent !== "object" || rawEvent === null) {
    const knownEvent = knownEvents.find(
      (event) => String(event.id) === String(rawEvent),
    );

    return (
      knownEvent ?? {
        id: rawEvent,
        name: `Combine #${rawEvent}`,
        date: "",
        city: "",
        state: "",
      }
    );
  }

  const id = getEventId(rawEvent);
  const knownEvent = knownEvents.find(
    (event) => String(event.id) === String(id),
  );
  const mergedEvent = { ...knownEvent, ...rawEvent, id };

  return {
    ...mergedEvent,
    id,
    name: getEventName(mergedEvent),
    date: getEventDate(mergedEvent),
    city: firstDefined(mergedEvent.city, ""),
    state: firstDefined(mergedEvent.state, ""),
  };
};

const normalizeBundle = (rawBundle, knownEvents) => {
  const rawCombines = getBundleCombines(rawBundle);
  const combines = (Array.isArray(rawCombines) ? rawCombines : []).map(
    (combine) => normalizeEvent(combine, knownEvents),
  );

  return {
    id: getBundleId(rawBundle),
    name: firstDefined(
      rawBundle?.name,
      rawBundle?.Name,
      rawBundle?.bundleName,
      "",
    ),
    discountType: firstDefined(
      rawBundle?.discountType,
      rawBundle?.DiscountType,
      "",
    ),
    amount: firstDefined(
      rawBundle?.amount,
      rawBundle?.Amount,
      rawBundle?.discountAmount,
      0,
    ),
    expirationDate: getBundleExpirationDate(rawBundle),
    combines,
    combineCount: getBundleCombineCount(rawBundle, combines),
  };
};

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;

  if (data?.error) {
    return data.error;
  }

  if (data?.message) {
    return data.message;
  }

  if (data?.detail) {
    return data.detail;
  }

  if (typeof data === "string") {
    return data;
  }

  if (data?.errors && typeof data.errors === "object") {
    const messages = Object.values(data.errors).flat().filter(Boolean);
    if (messages.length > 0) {
      return messages.join(" ");
    }
  }

  return fallback;
};

const toRequestId = (id) => {
  const numericId = Number(id);
  return Number.isNaN(numericId) ? id : numericId;
};

const getSelectedEventIds = (selectedCombines) =>
  selectedCombines
    .map((combine) => combine.id)
    .filter((id) => id !== undefined && id !== null && id !== "")
    .map(toRequestId);

const toApiPayload = (form, amount) => {
  const expirationDate = form.expirationDate || NEVER_EXPIRATION_DATE;

  return {
    Name: form.name.trim(),
    EventIds: getSelectedEventIds(form.selectedCombines),
    DiscountType: form.discountType,
    Amount: amount,
    ExpirationDate: expirationDate,
  };
};

const CombineBundleTable = forwardRef(function CombineBundleTable(_props, ref) {
  const apiInstance = useAxiosInstance();
  const [rawBundles, setRawBundles] = useState([]);
  const [combines, setCombines] = useState([]);
  const [form, setForm] = useState(EMPTY_FORM);
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(false);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [combinesLoading, setCombinesLoading] = useState(true);
  const [error, setError] = useState(null);

  const bundles = useMemo(
    () => rawBundles.map((bundle) => normalizeBundle(bundle, combines)),
    [rawBundles, combines],
  );

  const resetForm = useCallback(() => {
    setForm(EMPTY_FORM);
  }, []);

  const openCreateForm = useCallback(() => {
    resetForm();
    setEditing(false);
    setShowForm(true);
  }, [resetForm]);

  useImperativeHandle(
    ref,
    () => ({
      openCreateForm,
    }),
    [openCreateForm],
  );

  const fetchBundles = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getAllCombineBundles(apiInstance);
      setRawBundles(Array.isArray(data) ? data : []);
      setError(null);
    } catch (error) {
      setError(error.message || "Error getting combine bundles.");
    } finally {
      setLoading(false);
    }
  }, [apiInstance]);

  const fetchCombines = useCallback(async () => {
    setCombinesLoading(true);
    try {
      const data = await SearchEventsByParams(apiInstance, "");
      const events = Array.isArray(data)
        ? data
        : (data?.events ?? data?.items ?? data?.data ?? []);

      setCombines(
        Array.isArray(events)
          ? events
              .map((event) => normalizeEvent(event))
              .filter((event) => event.id)
          : [],
      );
    } catch (error) {
      toast.error(error?.message || "Error loading combines.");
    } finally {
      setCombinesLoading(false);
    }
  }, [apiInstance]);

  useEffect(() => {
    fetchBundles();
    fetchCombines();
  }, [fetchBundles, fetchCombines]);

  const selectedCombineIds = useMemo(
    () => new Set(form.selectedCombines.map((combine) => String(combine.id))),
    [form.selectedCombines],
  );

  const availableCombines = useMemo(
    () =>
      combines.filter(
        (combine) =>
          !selectedCombineIds.has(String(combine.id)) &&
          isTodayOrFuture(combine.date),
      ),
    [combines, selectedCombineIds],
  );

  const handleSelectCombine = (selectedId) => {
    if (selectedId === undefined || selectedId === null || selectedId === "") {
      return;
    }

    const selectedCombine = combines.find(
      (combine) => String(combine.id) === String(selectedId),
    );

    if (!selectedCombine) {
      return;
    }

    setForm((prev) => ({
      ...prev,
      selectedCombines: [...prev.selectedCombines, selectedCombine],
    }));
  };

  const handleRemoveCombine = (combineId) => {
    setForm((prev) => ({
      ...prev,
      selectedCombines: prev.selectedCombines.filter(
        (combine) => String(combine.id) !== String(combineId),
      ),
    }));
  };

  const handleFormSubmit = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error("Bundle name is required.");
      return;
    }

    if (getSelectedEventIds(form.selectedCombines).length < 2) {
      toast.error("Please select at least two combines.");
      return;
    }

    if (!form.discountType) {
      toast.error("Please select a discount type.");
      return;
    }

    const today = getLocalToday();
    if (form.expirationDate && form.expirationDate < today) {
      toast.error("Expiration date cannot be in the past.");
      return;
    }

    const amount = parseFloat(form.amount);
    if (form.amount === "" || Number.isNaN(amount)) {
      toast.error("Please enter a valid amount.");
      return;
    }

    if (amount <= 0) {
      toast.error("Amount must be greater than 0.");
      return;
    }

    if (form.discountType === PERCENT_OFF && amount > 100) {
      toast.error("Percentage cannot exceed 100%.");
      return;
    }

    try {
      const payload = toApiPayload(form, amount);

      if (editing) {
        await updateCombineBundle(apiInstance, form.id, payload);
        toast.success("Combine bundle updated successfully!");
      } else {
        await addCombineBundle(apiInstance, payload);
        toast.success("Combine bundle added successfully!");
      }

      await fetchBundles();
      resetForm();
      setShowForm(false);
      setEditing(false);
    } catch (error) {
      toast.error(
        getApiErrorMessage(
          error,
          editing
            ? "Failed to update combine bundle."
            : "Failed to add combine bundle.",
        ),
      );
    }
  };

  const handleEdit = (bundle) => {
    setForm({
      id: bundle.id,
      name: bundle.name,
      discountType: bundle.discountType,
      amount: bundle.amount,
      expirationDate: isNeverExpiration(bundle.expirationDate)
        ? ""
        : (bundle.expirationDate?.split("T")[0] ?? ""),
      selectedCombines: bundle.combines,
    });
    setEditing(true);
    setShowForm(true);
  };

  const handleDelete = async () => {
    const deleteId = confirmDeleteId;
    setConfirmDeleteId(null);

    try {
      await deleteCombineBundle(apiInstance, deleteId);
      setRawBundles((prev) =>
        prev.filter(
          (bundle) => String(getBundleId(bundle)) !== String(deleteId),
        ),
      );
      toast.success("Combine bundle deleted successfully!");
    } catch (error) {
      toast.error("Failed to delete combine bundle.");
    }
  };

  return (
    <>
      {loading ? (
        <div className="hidden">Loading...</div>
      ) : error ? (
        <div>Error: {error}</div>
      ) : bundles.length === 0 ? (
        <div className="border border-dashed border-gray-300 rounded p-6 text-center text-slate-600">
          No combine bundles have been created yet.
        </div>
      ) : (
        <div className="overflow-x-auto">
          <table className="min-w-full text-sm border border-gray-300">
            <thead className="bg-gray-100">
              <tr>
                <th className="border p-2 text-left">Bundle Name</th>
                <th className="border p-2 text-left">Combines</th>
                <th className="border p-2 text-left">Discount Type</th>
                <th className="border p-2 text-left">Expiration</th>
                <th className="border p-2 text-left min-w-44"></th>
              </tr>
            </thead>
            <tbody>
              {bundles.map((bundle) => (
                <tr key={bundle.id ?? bundle.name}>
                  <td className="border p-2 whitespace-nowrap">
                    {bundle.name}
                  </td>
                  <td className="border p-2 whitespace-nowrap">
                    {bundle.combineCount}
                  </td>
                  <td className="border p-2 whitespace-nowrap">
                    {formatDiscount(bundle)}
                  </td>
                  <td className="border p-2 whitespace-nowrap">
                    {isNeverExpiration(bundle.expirationDate)
                      ? "Never"
                      : utcToLocalDate(bundle.expirationDate)}
                  </td>
                  <td className="border p-2 whitespace-nowrap min-w-44">
                    <div className="flex items-center gap-2">
                      <button
                        type="button"
                        className="px-3 py-1 text-blue-700"
                        onClick={() => handleEdit(bundle)}
                      >
                        <FontAwesomeIcon icon={faEdit} className="mr-1" /> Edit
                      </button>
                      <button
                        type="button"
                        className="px-3 py-1 bg-red-500 text-white rounded hover:bg-red-700"
                        onClick={() => setConfirmDeleteId(bundle.id)}
                      >
                        <FontAwesomeIcon icon={faTrash} className="mr-1" />{" "}
                        Delete
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 p-4 z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-xl">
            <h3 className="text-lg font-semibold mb-4">
              {editing ? "Edit Bundle" : "Create New Bundle"}
            </h3>
            <form onSubmit={handleFormSubmit} className="space-y-5" noValidate>
              <div>
                <label className="block text-sm font-medium mb-1">
                  Bundle Name
                </label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      name: event.target.value,
                    }))
                  }
                  className="w-full border p-2 rounded"
                  required
                />
              </div>

              <div>
                <DropdownWithSearch
                  label={
                    <>
                      Select Combines <span className="text-red-600">*</span>
                    </>
                  }
                  options={availableCombines.map((combine) => ({
                    label: formatCombineLabel(combine),
                    value: combine.id,
                  }))}
                  selectedValues={[]}
                  placeholder={
                    combinesLoading ? "Loading combines..." : "Select combine"
                  }
                  onSelect={(selected) =>
                    handleSelectCombine(selected[0]?.value)
                  }
                  allowMultiple={false}
                  isLoading={combinesLoading}
                  isObjectArray={true}
                  containerClassName="relative w-full"
                  buttonClassName="w-full flex justify-between items-center border p-2 rounded shadow-sm bg-white"
                />
                {form.selectedCombines.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {form.selectedCombines.map((combine) => (
                      <span
                        key={combine.id}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-300 bg-gray-100 px-3 py-1 text-sm"
                      >
                        {formatCombineLabel(combine)}
                        <button
                          type="button"
                          className="font-semibold text-gray-600 hover:text-red-600"
                          onClick={() => handleRemoveCombine(combine.id)}
                        >
                          x
                        </button>
                      </span>
                    ))}
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Discount Type
                  </label>
                  <select
                    value={form.discountType}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        discountType: event.target.value,
                      }))
                    }
                    className="w-full border p-2 rounded"
                    required
                  >
                    <option value="">Select discount type</option>
                    <option value={AMOUNT_OFF}>{AMOUNT_OFF}</option>
                    <option value={PERCENT_OFF}>{PERCENT_OFF}</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1">
                    Amount
                  </label>
                  <input
                    type="number"
                    value={form.amount}
                    onChange={(event) =>
                      setForm((prev) => ({
                        ...prev,
                        amount: event.target.value,
                      }))
                    }
                    className="w-full border p-2 rounded"
                    required
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium mb-1">
                  Expiration
                </label>
                <input
                  type="date"
                  value={form.expirationDate}
                  min={getLocalToday()}
                  onChange={(event) =>
                    setForm((prev) => ({
                      ...prev,
                      expirationDate: event.target.value,
                    }))
                  }
                  className="w-full border p-2 rounded"
                />
              </div>

              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  className="px-4 py-2 border rounded"
                  onClick={() => {
                    setShowForm(false);
                    setEditing(false);
                  }}
                >
                  Close
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-red-500 text-white rounded"
                >
                  {editing ? "Update" : "Create Bundle"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {confirmDeleteId !== null && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 p-4 z-50">
          <div className="bg-white p-6 rounded shadow-md w-full max-w-sm">
            <h3 className="text-lg font-semibold mb-4">Confirm Delete</h3>
            <p>Are you sure you want to delete this combine bundle?</p>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                className="px-4 py-2 border rounded"
                onClick={() => setConfirmDeleteId(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                className="px-4 py-2 bg-red-600 text-white rounded"
                onClick={handleDelete}
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
});

export default CombineBundleTable;
